import React, { useState, useCallback, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { useSessionContext } from '../../context/sessionContext';
import { SUPPORT_EMAIL_ID } from '../../utils/helper';
import { fetchPlanUsageDetails } from '../../utils/services/planUsage';
import CreateTemplateModal from '../../components/CreateNewTemplate/CreateTemplateModal';
import CreateFlowModal from '../../components/CreateNewTemplate/CreateFlowModal';
import CreateBlockModal from '../../components/CreateNewTemplate/CreateBlockModal';
import SetupErrorModal from '../../components/Modals/SetupErrorModal';
import UpgradePlanModal from '../../components/Modals/UpgradePlanModal';
import { CREATE_NEW_TEMPLATE_MODAL } from '../../components/CreateNewTemplate/translationValues';
import { Icon } from 'antd';
import { NEW_TEMPLATE_SECTION } from './translationValues';
import {
  TopBarContainer,
  TopSection,
  Title,
  Subtitle,
  BottomSection,
  ButtonGroup,
  ButtonWrapper,
  PlusButton,
  TopBarViewButton,
} from './../NewTemplateSection/styles';

const NAMESPACES = ['CREATE_NEW_TEMPLATE_MODAL', 'NEW_TEMPLATE_SECTION'];

const TopBar = ({ selectedView, onViewChange, sidebarCollapsed }) => {
  const { t } = useTranslation(NAMESPACES);

  const titleMap = {
    documents: {
      title: t(NEW_TEMPLATE_SECTION.DOCUMENTS.key, NEW_TEMPLATE_SECTION.DOCUMENTS.value),
      description: t(
        NEW_TEMPLATE_SECTION.DOCUMENTS_DESC.key,
        NEW_TEMPLATE_SECTION.DOCUMENTS_DESC.value,
      ),
    },
    reusable_pages: {
      title: t(NEW_TEMPLATE_SECTION.REUSABLE_PAGES.key, NEW_TEMPLATE_SECTION.REUSABLE_PAGES.value),
      description: t(
        NEW_TEMPLATE_SECTION.REUSABLE_PAGES_DESC.key,
        NEW_TEMPLATE_SECTION.REUSABLE_PAGES_DESC.value,
      ),
    },
    reusable_sections: {
      title: t(
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
      ),
      description: t(
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS_DESC.key,
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS_DESC.value,
      ),
    },
  };

  const [showModal, setShowModal] = useState(null); // documents | reusable_pages | reusable_sections
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [planName, setPlanName] = useState('');
  const [documentLimit, setDocumentLimit] = useState(null);
  const [showSetupFailureError, setShowSetupFailureError] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const { isEnterprise, checkUserAccess, checkUserAccessRoles } = useContext(AppStoreContext);

  const organisationSelector = useCallback(store => store.organizationSetup, []);
  const [organizationSetup] = useSessionContext(organisationSelector);

  const isEmailContent = location.pathname === '/emailTemplates';
  const isSaasSetupFailed =
    !isEnterprise && checkUserAccess('client') && organizationSetup !== 'completed';

  const redirectToTemplate = (template, ...rest) => {
    const isComplex = rest[0] === 'isComplex';
    if (isComplex) {
      history.push(`/design/${template?.documentId}`);
    } else {
      if (template.id) {
        history.push(`/design/${template.id}`);
      }
    }
  };
  const redirectToFlow = id => {
    history.push(`/flow/${id}`);
  };
  const redirectToBlock = id => {
    history.push(`/block/${id}`);
  };

  const handleCreateDocTemplate = async () => {
    if (isSaasSetupFailed) {
      return setShowSetupFailureError(true);
    }
    if (isEnterprise || checkUserAccess('master')) {
      setShowModal('documents');
      return;
    }
    try {
      setIsCheckingLimit(true);
      const response = await fetchPlanUsageDetails();
      if (response?.statusCode === 1918) {
        const { templateLimit: created } = response.data.usage;
        const { templateLimit: limit, planName } = response.data.upperLimit;
        if (created >= limit) {
          setDocumentLimit(limit);
          setPlanName(planName);
          setShowUpgradeModal(true);
        } else {
          setShowModal('documents');
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  return (
    <TopBarContainer>
      <TopSection sidebarCollapsed={sidebarCollapsed}>
        <div>
          <Title>{titleMap[selectedView].title}</Title>
          <Subtitle>{titleMap[selectedView].description}</Subtitle>
        </div>
      </TopSection>

      <BottomSection sidebarCollapsed={sidebarCollapsed}>
        <ButtonGroup>
          <ButtonWrapper className="group">
            <PlusButton className="plus-button">
              {isCheckingLimit ? (
                <Icon type="loading" />
              ) : (
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (!isEmailContent) {
                      handleCreateDocTemplate();
                    } else {
                      setShowModal('documents');
                    }
                  }}
                />
              )}
            </PlusButton>
            <TopBarViewButton
              active={selectedView === 'documents'}
              view="documents"
              onClick={() => onViewChange('documents')}
            >
              {t(NEW_TEMPLATE_SECTION.DOCUMENTS.key, NEW_TEMPLATE_SECTION.DOCUMENTS.value)}
            </TopBarViewButton>
          </ButtonWrapper>

          <ButtonWrapper className="group">
            {checkUserAccessRoles(['REUSABLECONTENTMANAGER']) && (
              <PlusButton className="plus-button">
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (isSaasSetupFailed) setShowSetupFailureError(true);
                    else setShowModal('reusable_pages');
                  }}
                />
              </PlusButton>
            )}

            <TopBarViewButton
              active={selectedView === 'reusable_pages'}
              view="reusable_pages"
              onClick={() => onViewChange('reusable_pages')}
            >
              {t(NEW_TEMPLATE_SECTION.REUSABLE_PAGES.key, NEW_TEMPLATE_SECTION.REUSABLE_PAGES.value)}
            </TopBarViewButton>
          </ButtonWrapper>

          <ButtonWrapper className="group">
            {checkUserAccessRoles(['REUSABLECONTENTMANAGER']) && (
              <PlusButton className="plus-button">
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (isSaasSetupFailed) setShowSetupFailureError(true);
                    else setShowModal('reusable_sections');
                  }}
                />
              </PlusButton>
            )}
            <TopBarViewButton
              active={selectedView === 'reusable_sections'}
              view="reusable_sections"
              onClick={() => onViewChange('reusable_sections')}
            >
              {t(
                NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
                NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
              )}
            </TopBarViewButton>
          </ButtonWrapper>
        </ButtonGroup>
      </BottomSection>

      {showModal === 'documents' && (
        <CreateTemplateModal
          visible={true}
          onCancel={() => setShowModal(null)}
          onSucess={redirectToTemplate}
        />
      )}
      {showModal === 'reusable_pages' && (
        <CreateFlowModal onCancel={() => setShowModal(null)} onSucess={redirectToFlow} />
      )}
      {showModal === 'reusable_sections' && (
        <CreateBlockModal onCancel={() => setShowModal(null)} onSucess={redirectToBlock} />
      )}

      <UpgradePlanModal
        visible={showUpgradeModal}
        modalText={
          planName === 'Premium'
            ? t(
              CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.key,
              CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.value,
              { emailId: SUPPORT_EMAIL_ID },
            )
            : planName === 'Free'
              ? t(
                CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.key,
                CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.value,
                { documentLimit },
              )
              : t(
                CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.key,
                CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.value,
                { documentLimit },
              )
        }
        onCancel={() => setShowUpgradeModal(false)}
      />

      <SetupErrorModal showError={showSetupFailureError} setShowError={setShowSetupFailureError} />
    </TopBarContainer>
  );
};

export default TopBar;
